/**
 * Панель массовых операций (§5.7, §12.4).
 *
 * Появляется при выделении ≥ 1 строки. Выбор значения открывает
 * предохранитель: «Только пустые (9)» первой кнопкой — это основной режим
 * back-fill и единственная защита от молчаливого перетирания чужой работы.
 */
import { useMemo, useState } from 'react'
import type { CommentKind } from '@shared/types.ts'
import { Button, IconComment, Input, Menu, Popover, Textarea, type MenuItem } from '../../../components/index.js'
import { asOneOperation } from '../../../lib/api.js'
import { useApp } from '../../../store/app-store.js'
import { tableTexts as t } from '../texts.table.js'
import type { ColumnDef } from '../table/columns.js'
import { enumBadge } from '../table/enum-badges.js'
import { enumOptions, valueLabel, type ValueContext } from '../table/values.js'
import type { BulkEditApi } from './useBulkEdit.js'
import './BulkBar.css'

/**
 * Колонки, которыми засыпают дыры пачкой (§5.7.3).
 *
 * Сначала здесь были только справочники. Но «Доставка», «Установка» и
 * «Гарантия» у трёх десятков брендов заполняются одним и тем же текстом, и
 * вбивать его по одной строке — это ровно та работа, ради отмены которой
 * массовая правка и существует. Для таких колонок вместо списка значений
 * открывается поле ввода.
 */
const BULK_COLUMNS = [
  'tier', 'visibility', 'seoPriority', 'contextPriority',
  'url', 'delivery', 'installation', 'warranty', 'stockDisplay', 'oos',
] as const

/** Текстовая колонка: значение набирают, а не выбирают. */
function isTextColumn(column: ColumnDef): boolean {
  return column.editor === 'text' || column.editor === 'url'
}

export interface BulkBarProps {
  bulk: BulkEditApi
  columns: ColumnDef[]
  ctx: ValueContext
  onDone: (text: string) => void
}

interface Pending {
  column: ColumnDef
  value: string | null
  anchor: HTMLElement
}

export function BulkBar({ bulk, columns, ctx, onDone }: BulkBarProps) {
  const [picker, setPicker] = useState<{ column: ColumnDef; anchor: HTMLElement } | null>(null)
  const [pending, setPending] = useState<Pending | null>(null)
  /** Набранный текст для текстовых колонок. */
  const [text, setText] = useState('')
  /* Комментарий или задача сразу всем выбранным: «этим двенадцати — завести
     до пятницы» — обычная просьба, и щёлкать по каждому бренду ради неё
     никто не будет. */
  const [noteAnchor, setNoteAnchor] = useState<HTMLElement | null>(null)
  const [note, setNote] = useState('')
  const [noteKind, setNoteKind] = useState<CommentKind>('comment')
  const [noteDue, setNoteDue] = useState('')
  const [noteBusy, setNoteBusy] = useState(false)
  const addComment = useApp((state) => state.addComment)

  const available = useMemo(
    () => BULK_COLUMNS
      .map((code) => columns.find((column) => column.code === code))
      .filter((column): column is ColumnDef => column !== undefined),
    [columns],
  )

  if (bulk.count === 0) return null

  /* Значения показываем теми же чипами, что в таблице: простым текстом
     «A+ · A · B · C» выбирали вслепую, а цвет тут и есть смысл. */
  const items: MenuItem[] = picker && !isTextColumn(picker.column)
    ? enumOptions(ctx.enums, picker.column.enumSet).map((option) => {
      const badge = enumBadge(
        picker.column.enumSet,
        option.value,
        option.label,
        (ctx.enums[picker.column.enumSet ?? ''] ?? []).find((v) => v.value === option.value)?.color,
      )
      return { id: option.value, label: option.label, ...(badge ? { badge } : null) }
    })
    : []

  const census = pending ? bulk.census(pending.column) : null

  async function run(mode: 'empty' | 'all') {
    if (!pending) return
    const { column, value } = pending
    setPending(null)
    const written = await bulk.apply(column, value, mode)
    onDone(t.bulk.done(column.srTitle ?? column.title, written))
  }

  /** Одна запись каждому выбранному бренду — одной операцией, одним шагом. */
  async function addNote(): Promise<void> {
    const body = note.trim()
    if (body === '' || noteBusy) return
    setNoteBusy(true)
    try {
      const ids = [...bulk.selected]
      await asOneOperation(async () => {
        for (const brandId of ids) {
          await addComment(brandId, body, null, noteKind, noteKind === 'task' && noteDue ? noteDue : null)
        }
      })
      setNote('')
      setNoteDue('')
      setNoteKind('comment')
      setNoteAnchor(null)
      onDone(noteKind === 'task' ? t.bulk.taskDone(ids.length) : t.bulk.commentDone(ids.length))
    } finally {
      setNoteBusy(false)
    }
  }

  return (
    <div className="bulk-bar" role="region" aria-label={t.bulk.label}>
      <span className="bulk-bar__count">{t.bulk.selected(bulk.count)}</span>

      {/* Комментарий и задача — не значение колонки, поэтому стоят первыми
          и отдельно от списка полей. */}
      <Button
        variant="ghost"
        size="s"
        iconBefore={<IconComment size={16} />}
        disabled={bulk.running}
        onClick={(event) => setNoteAnchor(event.currentTarget)}
      >
        {t.bulk.note}
      </Button>

      {available.map((column) => (
        <Button
          key={column.code}
          variant="ghost"
          size="s"
          disabled={bulk.running}
          onClick={(event) => setPicker({ column, anchor: event.currentTarget })}
        >
          {t.bulk.pick(column.srTitle ?? column.title)}
        </Button>
      ))}

      {/* «Снять выделение» прижата к правому краю и выделена подложкой:
          это выход из режима, а не ещё одно поле для правки. */}
      <Button className="bulk-bar__clear" variant="secondary" size="s" onClick={bulk.clear}>
        {t.bulk.clear}
      </Button>

      {noteAnchor ? (
        <Popover open anchor={noteAnchor} onClose={() => setNoteAnchor(null)} role="dialog" minWidth={340} focusOnOpen>
          <div className="bulk-note">
            <div className="bulk-note__kinds">
              <button
                type="button"
                className={noteKind === 'comment' ? 'bulk-note__kind bulk-note__kind--on' : 'bulk-note__kind'}
                onClick={() => setNoteKind('comment')}
              >
                {t.comment.kindComment}
              </button>
              <button
                type="button"
                className={noteKind === 'task' ? 'bulk-note__kind bulk-note__kind--on bulk-note__kind--task' : 'bulk-note__kind'}
                onClick={() => setNoteKind('task')}
              >
                {t.comment.kindTask}
              </button>
              {noteKind === 'task' ? (
                <input
                  type="date"
                  className="bulk-note__date"
                  value={noteDue}
                  aria-label={t.comment.dueLabel}
                  onChange={(event) => setNoteDue(event.target.value)}
                />
              ) : null}
            </div>
            <Textarea
              rows={3}
              autoFocus
              value={note}
              placeholder={noteKind === 'task' ? t.comment.taskPlaceholder : t.comment.placeholder}
              aria-label={noteKind === 'task' ? t.comment.taskPlaceholder : t.comment.placeholder}
              onChange={(event) => setNote(event.target.value)}
            />
            <div className="bulk-note__actions">
              <span className="t-meta">{t.bulk.noteHint(bulk.count)}</span>
              <Button
                variant="primary"
                size="s"
                loading={noteBusy}
                disabled={note.trim() === ''}
                onClick={() => void addNote()}
              >
                {t.bulk.noteSubmit}
              </Button>
            </div>
          </div>
        </Popover>
      ) : null}

      {/* Текстовая колонка: вместо списка значений — поле ввода. Пустая
          строка очищает значение у всех выбранных. */}
      {picker && isTextColumn(picker.column) ? (
        <Popover
          open
          anchor={picker.anchor}
          onClose={() => { setPicker(null); setText('') }}
          role="dialog"
          minWidth={320}
          maxWidth={320}
        >
          <form
            className="bulk-guard"
            onSubmit={(event) => {
              event.preventDefault()
              const { column, anchor } = picker
              const trimmed = text.trim()
              setPicker(null)
              setText('')
              setPending({ column, value: trimmed === '' ? null : trimmed, anchor })
            }}
          >
            <p className="bulk-guard__title t-body">
              {t.bulk.pick(picker.column.srTitle ?? picker.column.title)}
            </p>
            <Input
              autoFocus
              value={text}
              aria-label={picker.column.srTitle ?? picker.column.title}
              placeholder={t.bulk.textPlaceholder}
              onChange={(event) => setText(event.target.value)}
            />
            <p className="bulk-guard__text t-meta">{t.bulk.textHint}</p>
            <div className="bulk-guard__actions">
              <Button type="submit" variant="secondary" size="m">{t.bulk.textApply}</Button>
              <Button
                variant="ghost"
                size="m"
                onClick={() => { setPicker(null); setText('') }}
              >
                {t.bulk.cancel}
              </Button>
            </div>
          </form>
        </Popover>
      ) : null}

      {picker && !isTextColumn(picker.column) ? (
        <Menu
          open
          anchor={picker.anchor}
          label={picker.column.srTitle ?? picker.column.title}
          items={items}
          onClose={() => setPicker(null)}
          onSelect={(id) => {
            const { column, anchor } = picker
            setPicker(null)
            setPending({ column, value: id, anchor })
          }}
        />
      ) : null}

      {pending && census ? (
        <Popover open anchor={pending.anchor} onClose={() => setPending(null)} role="dialog" minWidth={320} maxWidth={320}>
          <div className="bulk-guard">
            <p className="bulk-guard__title t-body">
              {t.bulk.target(pending.column.srTitle ?? pending.column.title, valueLabel(ctx, pending.column, pending.value))}
            </p>
            <p className="bulk-guard__text t-meta">
              {census.filled > 0
                ? t.bulk.appliesTo(census.total, census.filled)
                : t.bulk.appliesToClean(census.total)}
            </p>
            {census.total > 50 ? (
              <p className="bulk-guard__text t-meta">{t.bulk.bigPack(census.total)}</p>
            ) : null}
            <div className="bulk-guard__actions">
              <Button
                variant="secondary"
                size="m"
                disabled={census.empty === 0}
                loading={bulk.running}
                onClick={() => void run('empty')}
              >
                {t.bulk.onlyEmpty(census.empty)}
              </Button>
              <Button variant="secondary" size="m" loading={bulk.running} onClick={() => void run('all')}>
                {t.bulk.overwriteAll(census.total)}
              </Button>
            </div>
            {census.empty === 0 ? (
              <p className="bulk-guard__text t-meta">{t.bulk.nothingToFill}</p>
            ) : null}
            <Button variant="ghost" size="s" onClick={() => setPending(null)}>{t.bulk.cancel}</Button>
          </div>
        </Popover>
      ) : null}
    </div>
  )
}
